// Reusable people pickers shared by the ticket intake + detail screens: a
// chip list (★ primary, × remove) plus a name(/email) autocomplete add row.
// These are fully controlled — the parent owns the list and gets the full new
// set on every change — so they work both against a saved ticket and against
// local form state in the New ticket modal.

import { useState } from "react";
import { useApi } from "../lib/useApi";
import { listClients } from "../lib/api";
import type { Assignee, WorkTypeId, PeopleFieldId } from "../lib/types";

export interface RequesterPick {
  name: string;
  email: string | null;
}
export interface AssigneePick {
  user_id: string;
  name: string;
}

export function personInitials(name: string): string {
  return (
    name
      .trim()
      .split(/\s+/)
      .map((p) => p[0]?.toUpperCase() ?? "")
      .slice(0, 2)
      .join("") || "•"
  );
}

/** Multiple requesters: chips + a name/email add row with prior-client
 *  suggestions. Position 0 is the primary client. */
export function RequestersField({
  requesters,
  onChange,
  disabled,
}: {
  requesters: RequesterPick[];
  onChange: (list: RequesterPick[]) => void;
  disabled?: boolean;
}) {
  const [n, setN] = useState("");
  const [e, setE] = useState("");
  const clientsApi = useApi((signal) => listClients(signal), []);
  const clients = clientsApi.data?.clients ?? [];

  function pickName(value: string) {
    setN(value);
    const match = clients.find((c) => c.name.toLowerCase() === value.toLowerCase());
    if (match?.email) setE(match.email);
  }

  function add() {
    if (!n.trim()) return;
    onChange([...requesters, { name: n.trim(), email: e.trim() || null }]);
    setN("");
    setE("");
  }

  return (
    <div className="field">
      <span className="label">Requested By</span>
      {requesters.length > 0 ? (
        <ul className="person-list">
          {requesters.map((r, i) => (
            <li key={`${r.name}-${i}`} className="person-row">
              <span className="person-avatar" aria-hidden="true">{personInitials(r.name)}</span>
              <span className="person-main">
                <span className="person-name">{r.name}</span>
                {r.email ? <span className="person-email">{r.email}</span> : null}
              </span>
              {i === 0 ? <span className="person-primary">Primary</span> : null}
              <button
                type="button"
                className="person-remove"
                aria-label={`Remove ${r.name}`}
                disabled={disabled}
                onClick={() => onChange(requesters.filter((_, idx) => idx !== i))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="row" style={{ gap: 8 }}>
        <input
          type="text"
          value={n}
          onChange={(ev) => pickName(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === "Enter") {
              ev.preventDefault();
              add();
            }
          }}
          placeholder="Requester name"
          list="pp-client-suggestions"
          aria-label="Requester name"
          autoComplete="off"
          disabled={disabled}
          style={{ flex: 1 }}
        />
        <button className="btn small" type="button" style={{ flex: "0 0 auto" }} disabled={disabled || !n.trim()} onClick={add}>
          Add
        </button>
      </div>
      <datalist id="pp-client-suggestions">
        {clients.map((c) => (
          <option key={c.name} value={c.name}>
            {c.email ?? ""}
          </option>
        ))}
      </datalist>
    </div>
  );
}

/** Multiple assignees: chips + a name-autocomplete add row resolving the typed
 *  name/email to a roster user. Position 0 is the primary. */
export function AssigneesField({
  value,
  options,
  loading,
  onChange,
  disabled,
}: {
  value: AssigneePick[];
  options: Assignee[];
  loading?: boolean;
  onChange: (list: AssigneePick[]) => void;
  disabled?: boolean;
  /** Optional context for role-visibility — informational only; the actual
   *  role filtering happens where the parent fetches `options` via listAssignees. */
  workType?: WorkTypeId;
  field?: PeopleFieldId;
}) {
  const [q, setQ] = useState("");
  const ids = value.map((a) => a.user_id);
  const addable = options.filter((u) => !ids.includes(u.id));

  function add() {
    const v = q.trim().toLowerCase();
    if (!v) return;
    const match = options.find((u) => u.name.toLowerCase() === v || (u.email ?? "").toLowerCase() === v);
    if (match && !ids.includes(match.id)) {
      onChange([...value, { user_id: match.id, name: match.name }]);
      setQ("");
    }
  }

  return (
    <div className="field">
      <span className="label">Assignees</span>
      {value.length > 0 ? (
        <ul className="person-list">
          {value.map((a, i) => (
            <li key={a.user_id} className="person-row">
              <span className="person-avatar" aria-hidden="true">{personInitials(a.name)}</span>
              <span className="person-main">
                <span className="person-name">{a.name}</span>
              </span>
              {i === 0 ? <span className="person-primary">Primary</span> : null}
              <button
                type="button"
                className="person-remove"
                aria-label={`Remove ${a.name}`}
                disabled={disabled}
                onClick={() => onChange(value.filter((x) => x.user_id !== a.user_id))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      ) : null}
      <div className="row" style={{ gap: 8 }}>
        <input
          type="text"
          value={q}
          onChange={(ev) => setQ(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === "Enter") {
              ev.preventDefault();
              add();
            }
          }}
          placeholder="Assign by name…"
          list="pp-assignee-suggestions"
          aria-label="Add assignee by name"
          autoComplete="off"
          disabled={disabled || loading}
        />
        <button className="btn small" type="button" style={{ flex: "0 0 auto" }} disabled={disabled || loading || !q.trim()} onClick={add}>
          Add
        </button>
      </div>
      <datalist id="pp-assignee-suggestions">
        {addable.map((u) => (
          <option key={u.id} value={u.name}>
            {u.email ?? ""}
          </option>
        ))}
      </datalist>
      {loading ? <span className="hint">Loading team…</span> : null}
    </div>
  );
}
